import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { LayoutDashboard, Calendar, Receipt, User, LogOut, Menu, X, PiggyBank } from "lucide-react";
import { useEffect, useState } from "react";

export default function ClientLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.classList.toggle("scroll-locked", drawerOpen);
    return () => document.body.classList.remove("scroll-locked");
  }, [drawerOpen]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const items = [
    { to: "/portal", label: "Início", icon: LayoutDashboard, end: true },
    { to: "/portal/reservas", label: "Reservas", icon: Calendar },
    { to: "/portal/banco-de-horas", label: "Banco de Horas", icon: PiggyBank },
    { to: "/portal/faturas", label: "Pagamentos", icon: Receipt },
    { to: "/portal/perfil", label: "Perfil", icon: User },
  ];

  const renderSidebar = (scope) => (
    <>
      <div className="px-5 py-5 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-[#14213D] flex items-center justify-center">
            <span className="font-display font-bold text-[#D4AF37] text-sm">VM</span>
          </div>
          <div>
            <div className="font-display font-semibold text-[#14213D] text-sm">VM Corporate</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400">Portal do Cliente</div>
          </div>
        </div>
        {scope === "mobile" && (
          <button
            onClick={() => setDrawerOpen(false)}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500"
            aria-label="Fechar menu"
            data-testid="client-drawer-close"
          >
            <X size={20} />
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {items.map((it) => {
          const slug = it.to.split("/").pop() || "dashboard";
          const baseTid = `client-nav-${slug}`;
          const tid = scope === "mobile" ? `${baseTid}-m` : baseTid;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              onClick={() => setDrawerOpen(false)}
              className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`}
              data-testid={tid}
            >
              <it.icon size={16} />
              <span>{it.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-gray-100 p-3">
        <button
          onClick={async () => { setDrawerOpen(false); await logout(); navigate("/"); }}
          className="sidebar-nav-item w-full text-left"
          data-testid={scope === "mobile" ? "client-logout-btn-m" : "client-logout-btn"}
        >
          <LogOut size={16} /> Sair
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex" data-testid="client-layout">
      <aside className="hidden lg:flex w-64 bg-white border-r border-gray-200 flex-col fixed h-full z-30">
        {renderSidebar("desktop")}
      </aside>

      {drawerOpen && (
        <div
          className="mobile-drawer-overlay lg:hidden"
          onClick={() => setDrawerOpen(false)}
          data-testid="client-drawer-overlay"
        />
      )}
      <aside
        className={`lg:hidden fixed top-0 left-0 h-full w-72 max-w-[85vw] bg-white border-r border-gray-200 flex flex-col z-50 transition-transform duration-300 ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="client-drawer"
        aria-hidden={!drawerOpen}
      >
        {renderSidebar("mobile")}
      </aside>

      <div className="flex-1 w-full lg:ml-64 min-w-0">
        <div className="mobile-topbar lg:hidden">
          <button
            onClick={() => setDrawerOpen(true)}
            className="p-2 rounded-md hover:bg-gray-100 text-[#14213D]"
            aria-label="Abrir menu"
            data-testid="client-drawer-open"
          >
            <Menu size={22} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-[#14213D] flex items-center justify-center">
              <span className="font-display font-bold text-[#D4AF37] text-[11px]">VM</span>
            </div>
            <span className="font-display font-semibold text-[#14213D] text-sm">Meu Portal</span>
          </div>
          <div className="w-8 h-8 rounded-full bg-[#14213D] text-white flex items-center justify-center text-xs font-semibold" title={user?.name}>
            {user?.name?.split(" ").map(n => n[0]).slice(0, 2).join("")}
          </div>
        </div>

        <header className="hidden lg:flex h-16 bg-white border-b border-gray-200 px-4 sm:px-6 md:px-8 items-center justify-between sticky top-0 z-30">
          <div className="text-sm text-gray-500 truncate">
            Olá, <span className="text-[#14213D] font-semibold">{user?.name}</span>
          </div>
          <div className="w-9 h-9 rounded-full bg-[#14213D] text-white flex items-center justify-center text-xs font-semibold">
            {user?.name?.split(" ").map(n => n[0]).slice(0, 2).join("")}
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:p-8 admin-content-area">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
